import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';
//
import {
  isValidToken,
  setSession,
  setAuthCookies,
  globalLogout,
  getCookies,
  setCookies,
  getAccessToken,
  isTokenExpired,
} from './utils';
import {
  ActionMapType,
  AuthStateType,
  PreSignUpUserType,
  AuthUserType,
  JWTContextType,
} from './types';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  UPDATE = 'UPDATE',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  // RESENTREGISTERCODE = 'RESENTREGISTERCODE',
  VERIFYREGISTER = 'VERIFYREGISTER',
  RESETPASSWORD = 'RESETPASSWORD',
  CONFIRMRESETPASSWORD = 'CONFIRMRESETPASSWORD',
  // VERIFYNEWPASSWORD = 'VERIFYNEWPASSWORD',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: {
    isAuthenticated: boolean;
    user: AuthUserType;
    // preSignUpUser: PreSignUpUserType;
  };
  [Types.UPDATE]: {
    // isAuthenticated: boolean;
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    // isAuthenticated: boolean;
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    // user: AuthUserType;
    email: string;
    verifyToken: string;
    preSignUpUser: PreSignUpUserType;
  };
  [Types.VERIFYREGISTER]: {
    isAuthenticated: boolean;
    user: AuthUserType;
  };
  [Types.RESETPASSWORD]: undefined;
  [Types.CONFIRMRESETPASSWORD]: {
    isAuthenticated: boolean;
    user: AuthUserType;
  };
  // [Types.RESETPASSWORD]: {
  //   verifyToken: string;
  // };
  // [Types.CONFIRMRESETPASSWORD]: {
  //   user: AuthUserType;
  // };
  // [Types.VERIFYNEWPASSWORD]: {
  //   user: AuthUserType;
  // };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
  preSignUpUser: null,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
      preSignUpUser: null,
    };
  }
  if (action.type === Types.UPDATE) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
      // preSignUpUser: null,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      email: action.payload.email,
      verifyToken: action.payload.verifyToken,
      preSignUpUser: action.payload.preSignUpUser,
      // preSignUpUser: null,
      // isAuthenticated: true,
      // user: action.payload.user,
    };
  }
  if (action.type === Types.VERIFYREGISTER) {
    return {
      ...state,
      // isAuthenticated: false,
      // user: null,
      preSignUpUser: null,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
      // status: action.payload.status,
    };
  }
  if (action.type === Types.RESETPASSWORD) {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  if (action.type === Types.CONFIRMRESETPASSWORD) {
    return {
      ...state,
      // isAuthenticated: false,
      isAuthenticated: action.payload.isAuthenticated,
      // user: null,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const storageAvailable = localStorageAvailable();

  const initialize = useCallback(async () => {
    try {
      // const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const idTokenFromCookies = getCookies('jwt.id', false);
      // const accessToken = storageAvailable ? '' : '';
      // const accessToken = '';

      // console.log('hello');
      // if (false) {
      if (idTokenFromCookies) {
        const isValidSession = await isValidToken(idTokenFromCookies);
        // console.log(isValidSession);
        // setSession(accessToken);

        if (isValidSession) {
          const response = await axios.get('/myaccount');

          // console.log(response);

          const { user } = response.data;

          const userProfileURL = user?.profileURL;
          setCookies('userProfileURL', userProfileURL);
          console.log('initialized');

          dispatch({
            type: Types.INITIAL,
            payload: {
              isAuthenticated: true,
              user,
              // preSignUpUser: null,
            },
          });
        } else {
          dispatch({
            type: Types.INITIAL,
            payload: {
              isAuthenticated: false,
              user: null,
              // preSignUpUser: null,
            },
          });
        }
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: false,
            user: null,
            // preSignUpUser: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          isAuthenticated: false,
          user: null,
          // preSignUpUser: null,
        },
      });
    }
  }, []);
  // }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email: string, password: string) => {
    // const response = await axios.post('/api/account/login', {
    const response = await axios.post('/signin', {
      email,
      password,
    });
    const { accessToken, user, status, username, userId, idToken, refreshToken, expiresIn } =
      response.data;
    // localStorage.setItem('accessToken', idToken);

    // console.log(user);

    const userProfileURL = user?.profileURL;
    setCookies('userProfileURL', userProfileURL);
    // console.log('initialized');

    setSession(idToken);
    setAuthCookies({
      idToken,
      refreshToken,
      accessToken,
      // username,
      // userId,
      expiresIn,
    });
    dispatch({
      type: Types.LOGIN,
      payload: {
        user,
      },
    });
  }, []);

  // REGISTER
  const register = useCallback(
    async (
      email: string,
      password: string,
      userName: string,
      given_name: string,
      family_name: string
    ) => {
      try {
        const response = await axios.post('/signup', {
          email,
          password,
          userName,
          given_name,
          family_name,
        });

        // // console.log(response);
        // const { verifyToken, user } = response.data;
        const { verifyToken, userId } = response.data;
        // const { verifyToken, userId, preSignUpUser } = response.data;

        const preSignUpUser = {
          email,
          password,
        };

        // console.log(response.data);
        // const verifyToken = 'sedfsdgvfghbdfthsdtfhsdgfhsrfgh';

        if (verifyToken) {
          sessionStorage.setItem('userEmail', email);
          sessionStorage.setItem('verifyToken', verifyToken);
          // localStorage.setItem('verifyToken', verifyToken);

          dispatch({
            type: Types.REGISTER,
            payload: {
              // user,
              preSignUpUser,
              email,
              verifyToken,
            },
          });
        } else {
          throw new Error('no token available');
        }
      } catch (error) {
        console.log(error);
      }
    },
    []
  );

  const updateUser = useCallback((user: AuthUserType) => {
    dispatch({
      type: Types.UPDATE,
      payload: {
        user,
      },
    });
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    // setSession(null);？

    try {
      const accessTokenFromCookies = getCookies('jwt.access');
      if (!accessTokenFromCookies) throw new Error(`Access token is expired`);
      const isExpired = isTokenExpired(accessTokenFromCookies);
      if (isExpired) throw new Error(`Access token is expired`);

      // const accessTokenFromCookies = await getAccessToken();
      const response = await axios.post('/logout', {
        accessToken: accessTokenFromCookies,
      });
      const { status } = response.data;
      globalLogout();
      dispatch({
        type: Types.LOGOUT,
      });
    } catch (error) {
      console.log(error);
      globalLogout();
      dispatch({
        type: Types.LOGOUT,
      });
    }
  }, []);

  const verifyRegister = useCallback(
    async (email: string, code: string, preSignUpUser?: PreSignUpUserType) => {
      const response = await axios.post('/verify-register', {
        email,
        code,
        preSignUpUser,
      });

      // console.log(response);
      const { accessToken, user, status, username, userId, idToken, refreshToken, expiresIn } =
        response.data;
      // // const verifyToken = 'sedfsdgvfghbdfthsdtfhsdgfhsrfgh';
      // // sessionStorage.setItem('verifyToken', verifyToken);
      // // sessionStorage.setItem('verifyToken', verifyToken);
      // // sessionStorage.setItem('verifyToken', verifyToken);

      if (status === 'success') {
        if (accessToken && idToken && user) {
          // localStorage.setItem('accessToken', idToken);
          setSession(idToken);

          // const response = await axios.post('/logout', {
          //   email,
          //   password,
          // });

          setAuthCookies({
            idToken,
            refreshToken,
            accessToken,
            // username,
            // userId,
            expiresIn,
          });

          dispatch({
            type: Types.VERIFYREGISTER,
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: Types.VERIFYREGISTER,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      }
    },
    []
  );
  const resetPassword = useCallback(async (email: string) => {
    const response = await axios.post('/reset-password', {
      email,
    });
    // // console.log(response);
    const { verifyToken } = response.data;
    // const verifyToken = 'sedfsdgvfghbdfthsdtfhsdgfhsrfgh';

    sessionStorage.setItem('verifyToken', verifyToken);
    sessionStorage.setItem('email-recovery', email);
    dispatch({
      type: Types.RESETPASSWORD,
      // payload: {
      //   verifyToken,
      // },
    });
  }, []);

  const confirmResetPassword = useCallback(
    async (email: string, code: string, password: string) => {
      const response = await axios.post('/confirm-reset-password', {
        email,
        code,
        password,
      });
      // console.log(response);
      const { accessToken, user, status, username, userId, idToken, refreshToken, expiresIn } =
        response.data;

      if (status === 'success') {
        if (accessToken && idToken && user) {
          setSession(idToken);
          setAuthCookies({
            idToken,
            refreshToken,
            accessToken,
            // username,
            // userId,
            expiresIn,
          });
          dispatch({
            type: Types.CONFIRMRESETPASSWORD,
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: Types.CONFIRMRESETPASSWORD,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      }
    },
    []
  );
  // const verifyNewPassword = useCallback(
  //   async (email: string, verifyToken: string, password: string) => {},
  //   []
  // );

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      preSignUpUser: state.preSignUpUser,
      user: state.user,
      method: 'jwt',
      login,
      updateUser,
      // loginWithGoogle: () => {},
      // loginWithGithub: () => {},
      // loginWithTwitter: () => {},
      // loginWithFacebook: () => {},

      register,
      logout,
      verifyRegister,
      resetPassword,
      confirmResetPassword,
      // verifyNewPassword,
    }),
    [
      state.isAuthenticated,
      state.isInitialized,
      state.preSignUpUser,
      state.user,
      login,
      updateUser,
      register,
      logout,
      verifyRegister,
      resetPassword,
      confirmResetPassword,
      // verifyNewPassword,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
